import apiClient, { isRetryableApiError } from "@/lib/api";

const PILOTAGE_BASE_PATH = "/admin/pilotage";

const request = async (config, { retry = 0, notifyChange = true } = {}) => {
  let attempt = 0;

  while (true) {
    try {
      const response = await apiClient(config);
      if (notifyChange && config.method !== "get" && typeof window !== "undefined") {
        window.dispatchEvent(new Event("pilotage-data-changed"));
      }
      return response.data;
    } catch (error) {
      if (attempt >= retry || !isRetryableApiError(error)) {
        throw error;
      }

      attempt += 1;
    }
  }
};

const get = (path, params, options) =>
  request({
    url: path,
    method: "get",
    params,
  }, options);

const post = (path, data, config = {}) =>
  request({
    url: path,
    method: "post",
    data,
    ...config,
  });

const put = (path, data) =>
  request({
    url: path,
    method: "put",
    data,
  });

const remove = (path) =>
  request({
    url: path,
    method: "delete",
  });

export const COLLABORATOR_STATUSES = ["RECRUITMENT", "PLANNED", "ACTIVE", "NOTICE", "LEFT", "INACTIVE"];
export const CONTRACT_KINDS = ["CDI", "CDD", "ALTERNANT", "FREELANCE", "PORTAGE", "SUB_CONTRACTOR"];
export const MISSION_STATUSES = ["ACTIVE", "PLANNED", "ENDED"];
export const EXPENSE_CATEGORIES = [
  "TRANSPORT",
  "MEAL",
  "OTHER",
  "DEPLACEMENT",
  "HOTEL",
  "REPAS",
  "MATERIEL",
  "LICENCE",
  "ONBOARDING",
  "FORMATION",
  "PRIME",
  "TELEPHONE",
  "EQUIPEMENT",
  "PORTAGE",
  "ADMINISTRATIF"
];
export const EXPENSE_MODES = ["ONE_TIME", "MONTHLY"];
export const EXPENSE_STATUSES = ["SUBMITTED", "APPROVED", "REJECTED"];

export const listClients = () => get(`${PILOTAGE_BASE_PATH}/clients`);
export const getClient = (id) => get(`${PILOTAGE_BASE_PATH}/clients/${id}`);
export const createClient = (payload) => post(`${PILOTAGE_BASE_PATH}/clients`, payload);
export const updateClient = (id, payload) => put(`${PILOTAGE_BASE_PATH}/clients/${id}`, payload);
export const deleteClient = (id) => remove(`${PILOTAGE_BASE_PATH}/clients/${id}`);

export const listCollaborators = () =>
  get(`${PILOTAGE_BASE_PATH}/collaborators`, undefined, { retry: 1 });
export const getCollaborator = (id) => get(`${PILOTAGE_BASE_PATH}/collaborators/${id}`);
export const createCollaborator = (payload) => post(`${PILOTAGE_BASE_PATH}/collaborators`, payload);
export const updateCollaborator = (id, payload) =>
  put(`${PILOTAGE_BASE_PATH}/collaborators/${id}`, payload);
export const deleteCollaborator = (id) => remove(`${PILOTAGE_BASE_PATH}/collaborators/${id}`);

export const listMissions = (params) =>
  get(`${PILOTAGE_BASE_PATH}/missions`, params, { retry: 1 });
export const getMission = (id) =>
  get(`${PILOTAGE_BASE_PATH}/missions/${id}`, undefined, { retry: 1 });
export const getMissionFinancials = (id) =>
  get(`${PILOTAGE_BASE_PATH}/missions/${id}/financials`, undefined, { retry: 1 });
export const createMission = (payload) => post(`${PILOTAGE_BASE_PATH}/missions`, payload);
export const updateMission = (id, payload) => put(`${PILOTAGE_BASE_PATH}/missions/${id}`, payload);
export const deleteMission = (id) => remove(`${PILOTAGE_BASE_PATH}/missions/${id}`);

export const listTimesheets = (params) => get(`${PILOTAGE_BASE_PATH}/timesheets`, params);
export const listTimesheetMonths = (missionId) =>
  get(`${PILOTAGE_BASE_PATH}/timesheets/months`, { mission_id: missionId }, { retry: 1 });
export const createTimesheet = (payload) => post(`${PILOTAGE_BASE_PATH}/timesheets`, payload);
export const updateTimesheet = (id, payload) =>
  put(`${PILOTAGE_BASE_PATH}/timesheets/${id}`, payload);
export const lockTimesheet = (id) => post(`${PILOTAGE_BASE_PATH}/timesheets/${id}/lock`);

export const listExpenses = (params) =>
  get(`${PILOTAGE_BASE_PATH}/expenses`, params, { retry: 1 });
export const createExpense = (payload) => post(`${PILOTAGE_BASE_PATH}/expenses`, payload);
export const updateExpense = (id, payload) => put(`${PILOTAGE_BASE_PATH}/expenses/${id}`, payload);
export const deleteExpense = (id) => remove(`${PILOTAGE_BASE_PATH}/expenses/${id}`);
export const approveExpense = (id) => post(`${PILOTAGE_BASE_PATH}/expenses/${id}/approve`);
export const rejectExpense = (id) => post(`${PILOTAGE_BASE_PATH}/expenses/${id}/reject`);
export const getExpensesByCollaborator = (params) => get(`${PILOTAGE_BASE_PATH}/expenses/by-collaborator`, params);

export const listFixedCosts = () => get(`${PILOTAGE_BASE_PATH}/fixed-costs`, undefined, { retry: 1 });
export const createFixedCost = (payload) => post(`${PILOTAGE_BASE_PATH}/fixed-costs`, payload);
export const updateFixedCost = (id, payload) => put(`${PILOTAGE_BASE_PATH}/fixed-costs/${id}`, payload);
export const deleteFixedCost = (id) => remove(`${PILOTAGE_BASE_PATH}/fixed-costs/${id}`);

export const getDashboardFinancials = () => get(`${PILOTAGE_BASE_PATH}/dashboard/financials`);
export const getRealizedRevenue = (params) =>
  get(`${PILOTAGE_BASE_PATH}/financials/revenue-realized`, params, { retry: 1 });
export const getPilotageOverview = () => get(`${PILOTAGE_BASE_PATH}/dashboard/overview`);
export const getPilotageCockpit = (params) => get(`${PILOTAGE_BASE_PATH}/cockpit`, params, { retry: 1 });
export const getExecutiveCockpit = (params) =>
  get(`${PILOTAGE_BASE_PATH}/cockpit/executive`, params, { retry: 1 });
export const getSimulationDefaults = () => get(`${PILOTAGE_BASE_PATH}/simulation/defaults`);
export const computeSimulation = (payload, config) =>
  post(`${PILOTAGE_BASE_PATH}/simulation/compute`, payload, config);

export const listObjectiveYears = () => get(`${PILOTAGE_BASE_PATH}/objectives/years`);
export const listObjectivesByYear = (year) => get(`${PILOTAGE_BASE_PATH}/objectives/${year}`);
export const createObjective = (year, payload) => post(`${PILOTAGE_BASE_PATH}/objectives/${year}`, payload);
export const updateObjective = (id, payload) => put(`${PILOTAGE_BASE_PATH}/objectives/${id}`, payload);
export const deleteObjective = (id) => remove(`${PILOTAGE_BASE_PATH}/objectives/${id}`);
export const getObjectiveStatus = (id) => get(`${PILOTAGE_BASE_PATH}/objectives/${id}/status`);

// Deprecated legacy helpers (scenario-based endpoints).
export const upsertObjective = (year, scenario, payload) =>
  put(`${PILOTAGE_BASE_PATH}/objectives/${year}/${scenario}`, payload);
export const deleteObjectiveByScenario = (year, scenario) =>
  remove(`${PILOTAGE_BASE_PATH}/objectives/${year}/${scenario}`);
export const getObjectiveStatusByScenario = (year, scenario) =>
  get(`${PILOTAGE_BASE_PATH}/objectives/${year}/${scenario}/status`);

export const previewMonthlyObjective = payload => request({
  url: `${PILOTAGE_BASE_PATH}/objectives/monthly-preview`, method: "post", data: payload,
}, {notifyChange: false});
export const proposeMonthlyDistribution = id => get(`${PILOTAGE_BASE_PATH}/objectives/${id}/monthly-distribution`);
export const getMonthlyObjectives = (year, month, objectiveId) => get(`${PILOTAGE_BASE_PATH}/objectives/${year}/monthly`, {month, objective_id: objectiveId || undefined});
